import Cookies from 'js-cookie';
import { navigateToAccessDenied, navigateToLogin } from './navigation';

/**
 * Verifica as permissões do usuário baseado no status da resposta HTTP
 * Redireciona automaticamente em caso de erro de autenticação ou autorização
 * @param {Response} request - Objeto Response da requisição HTTP
 */
const verifyPermissionsOfUser = request => {
  if (request.status === 403) {
    navigateToAccessDenied();
    return;
  } else if (request.status === 401) {
    // Remove o token do cookie quando receber 401 (token expirado)
    Cookies.remove('token');
    // Redireciona para login
    navigateToLogin();
    return;
  }
};

const BASE_URL = `${process.env.REACT_APP_BASE_URL}/v1`;
const http = {
  post: async (body = {}, endpoint) => {
    const request = await fetch(BASE_URL + endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : null,
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`,
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
    });
    verifyPermissionsOfUser(request);

    return request.status == 204 ? request : await request.json();
  },

  postWithNoAuth: async (body = {}, endpoint) => {
    const request = await fetch(BASE_URL + endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : null,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
    });

    return request.status == 204 ? request : await request.json();
  },

  postWithoutBodyInFormatText: async endpoint => {
    const request = await fetch(BASE_URL + endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`,
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
    });
    verifyPermissionsOfUser(request);

    return request.status == 200 ? await request.text() : request;
  },

  postWithoutBodyInFormatJson: async endpoint => {
    const request = await fetch(BASE_URL + endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`,
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
    });
    verifyPermissionsOfUser(request);

    return request.ok ? await request.json() : request;
  },

  postFile: async (formData, endpoint) => {
    const request = await fetch(BASE_URL + endpoint, {
      method: 'POST',
      body: formData,
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`
      }
    });
    verifyPermissionsOfUser(request);

    return request;
  },

  postFormdata: async (formData, endpoint) => {
    const request = await fetch(BASE_URL + endpoint, {
      method: 'POST',
      body: formData,
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`
      }
    });
    verifyPermissionsOfUser(request);

    return request.status == 204 ? request : await request.json();
  },

  postExportFile: async endpoint => {
    const request = await fetch(BASE_URL + endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`,
        'Content-Type': 'application/json'
      }
    });
    verifyPermissionsOfUser(request);

    return request;
  },

  postExport: async (body, endpoint) => {
    const request = await fetch(BASE_URL + endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`,
        'Content-Type': 'application/json'
      }
    });
    verifyPermissionsOfUser(request);

    return request;
  },

  get: async endpoint => {
    const request = await fetch(BASE_URL + endpoint, {
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`,
        Accept: 'application/json'
      }
    });

    verifyPermissionsOfUser(request);
    return request.status == 200 ? await request.json() : request;
  },

  getInFormatText: async endpoint => {
    const request = await fetch(BASE_URL + endpoint, {
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`,
        Accept: '*/*'
      }
    });
    verifyPermissionsOfUser(request);

    return request.status == 200 ? await request.text() : request;
  },

  getWithAccept: async (endpoint, accept) => {
    const request = await fetch(BASE_URL + endpoint, {
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`,
        Accept: accept
      }
    });
    verifyPermissionsOfUser(request);

    return request;
  },

  getPdf: async endpoint => {
    const request = await fetch(BASE_URL + endpoint, {
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`,
        'Content-Type': 'application/pdf'
      }
    });
    verifyPermissionsOfUser(request);

    return request;
  },

  put: async (body, endpoint) => {
    let request = await fetch(BASE_URL + endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`,
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
    });
    verifyPermissionsOfUser(request);

    return request.status === 204 ? request : await request.json();
  },

  putFile: async (file, endpoint) => {
    let request = await fetch(BASE_URL + endpoint, {
      method: 'PUT',
      body: file,
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`
      }
    });
    verifyPermissionsOfUser(request);

    return request.status === 204 ? request : await request.json();
  },

  patch: async (body, endpoint) => {
    let request = await fetch(BASE_URL + endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`,
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
    });
    verifyPermissionsOfUser(request);

    return request.status === 204 ? request : await request.json();
  },

  putWithoutBody: async endpoint => {
    const request = await fetch(BASE_URL + endpoint, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`,
        Accept: 'application/json'
      }
    });
    verifyPermissionsOfUser(request);

    return request.status == 204 ? request : await request.json();
  },

  putWithoutBodyInFormatText: async endpoint => {
    const request = await fetch(BASE_URL + endpoint, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`,
        Accept: 'application/json'
      }
    });
    verifyPermissionsOfUser(request);

    return request.ok ? await request.text() : await request.json();
  },

  del: async endpoint => {
    const request = await fetch(BASE_URL + endpoint, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`,
        Accept: 'application/json'
      }
    });
    verifyPermissionsOfUser(request);

    return request.status == 204 ? request : await request.json();
  },

  delWithBody: async (body, endpoint) => {
    const request = await fetch(BASE_URL + endpoint, {
      method: 'DELETE',
      body: JSON.stringify(body),
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`,
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    });
    verifyPermissionsOfUser(request);

    return request.status == 204 ? request : await request.json();
  }
};

/**
 * Versão alternativa dos métodos HTTP que não redireciona em caso de erro 403
 * Utilizado em endpoints onde o usuário não deve perder dados digitados
 */
const httpWithoutRedirect = {
  post: async (body = {}, endpoint) => {
    const request = await fetch(BASE_URL + endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : null,
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`,
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
    });

    // Não chama verifyPermissionsOfUser para evitar redirecionamento
    // Apenas remove token em caso de 401
    if (request.status === 401) {
      Cookies.remove('token');
      navigateToLogin();
      return;
    }

    return request.status == 204 ? request : await request.json();
  },

  put: async (body, endpoint) => {
    let request = await fetch(BASE_URL + endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`,
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
    });

    // Não chama verifyPermissionsOfUser para evitar redirecionamento
    // Apenas remove token em caso de 401
    if (request.status === 401) {
      Cookies.remove('token');
      navigateToLogin();
      return;
    }

    return request.status === 204 ? request : await request.json();
  }
};

export default () => http;
export const useHttpWithoutRedirect = () => httpWithoutRedirect;
